import React, {useMemo} from "react";

import useDashboardStore from "store/dashboard-store";
import useTimeState from "hooks/dashboard/useTimeState";
import {LocalDateTime} from "@js-joda/core";
import {useGetDataInfoRange, useGetRecentData} from "api/api-hooks/datasets-hooks";
import {isNumeric} from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import {AdvancedProps, SparkLineProps, Usages} from "./SparkLineTypes";
import {defaultFromDateTime, defaultToDateTime} from "../charts/utils";
import {useCompanyTimezone} from "hooks/useTimezone";

export default function SparkLine(props: SparkLineProps) {
  const count = props.count || 14;
  const isEditMode = useDashboardStore(state => state.isEditMode);

  const companyTimezone = useCompanyTimezone();

  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const defaultFromDate =
    timeState.from ??
    defaultFromDateTime({
      zoneId: companyTimezone,
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...props.loadConf,
      },
      referenceTime: props.referenceTime,
    });

  const defaultToDate =
    timeState.to ??
    defaultToDateTime({
      zoneId: companyTimezone,
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...props.loadConf,
      },
      referenceTime: props.referenceTime,
    });

  const refetchInterval = props.updateFrequency && !isEditMode ? props.updateFrequency * 1000 : 0;

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    refTime:
      props.referenceTime &&
      LocalDateTime.parse(props.referenceTime).atZone(companyTimezone).toString(),
    options: {
      refetchInterval,
      enabled: !!props.dataset && !props.useLoadConf,
    },
  });

  const { data: dataRange, isLoading: isDataRangeLoading } = useGetDataInfoRange(
    [dataset],
    {
      timeStart: defaultFromDate?.toISOString(),
      timeEnd: defaultToDate?.toISOString(),
      downsampleInterval: props.downsampleInterval,
      useDefaultDownsampleInterval: true
    },
    {
      refetchInterval,
      enabled: !!props.dataset && props.useLoadConf && !!defaultFromDate && !!defaultToDate,
      
    }
  );

  const sortedData = useMemo(() => {
    if (props.useLoadConf) {
      return dataRange?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
    }
    return data?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
  }, [data, dataRange, props.useLoadConf]);

  const transformedData = useMemo((): {
    dataArray: number[];
    lastValueTime: null | Date;
    unit?: string;
    decimalPlaces?: number;
    title?: string;
  } => {
    if (!props.useLoadConf && !data?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null };
    if (props.useLoadConf && !dataRange?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null };

    const values = sortedData.map(d => d.value);
    const lastValueTime = new Date(sortedData[sortedData.length - 1].key);
    const unit = data?.[0]?.unit ?? dataRange?.[0]?.unit ?? "";
    const decimalPlaces =
      props.decimalPlaces ?? data?.[0]?.decimalPlaces ?? dataRange?.[0]?.decimalPlaces ?? 0;
    const title = props.useLoadConf ? data?.[0]?.title : dataRange?.[0]?.title;

    return { dataArray: values, lastValueTime, unit, decimalPlaces, title };
  }, [data, dataRange, sortedData, props.useLoadConf, props.decimalPlaces]);

  const formattedData = useMemo(() => {
    return sortedData.map(d => ({ y: d.value, x: new Date(d.key) }));
  }, [sortedData]);

  // props from legacy config
  const legacyProps = {
    // @ts-expect-error
    constant: props.constant,
    // @ts-expect-error
    dataset: props.baselineDataset,
    // @ts-expect-error
    usage: (props.useConstant ? "constant" : "dataset") as Usages,
    // @ts-expect-error
    color: props.baselineColor,
    // @ts-expect-error
    width: props.baselineWidth,
    // @ts-expect-error
    fillAboveColor: props.fillAboveColor,
    // @ts-expect-error
    fillBelowColor: props.fillBelowColor,
  };

  const getFromDate = () => {
    if (props.useLoadConf) {
      return defaultFromDate;
    }
    const fromDate = data?.[0].data[0].key;
    return fromDate ? new Date(fromDate) : new Date();
  };

  const getToDate = () => {
    if (props.useLoadConf) {
      return defaultToDate;
    }
    const dataArray = data?.[0].data;
    const toDate = dataArray?.[dataArray.length - 1]?.key;

    return toDate ? new Date(toDate) : new Date();
  };

  const isMultipleChartsInOneBox =
    props.multipleCharts?.length > 0 &&
    props.isMultipleCharts &&
    props.generalProperties?.chartsInOneBox;

  const advancedProps: AdvancedProps = {
    baseline: {
      ...legacyProps,
      ...props.baseline,
    },
    data: formattedData,
    minLine: props.minLine,
    maxLine: props.maxLine,
    count,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
    showYAxis: props.showYAxis,
    fromDate: getFromDate(),
    toDate: getToDate(),
    isMultipleCharts: props.isMultipleCharts,
    isExpanded: props.isExpanded,
    setExpandedChartIndex: props.setExpandedChartIndex,
    multipleCharts: isMultipleChartsInOneBox ? props.multipleCharts : [],
    allCharts: props.allCharts,
    isMultipleChartsInOneBox,
    useDecimalsFromHundred: props.useDecimalsFromHundred,
    downsampleInterval: props.downsampleInterval
  };

  return (
    <SparkLineHandler
      isLoading={isLoading || isDataRangeLoading}
      dataArray={transformedData.dataArray}
      chartIndex={props.chartIndex}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={transformedData.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      chartTitle={props.chartTitle}
      unit={transformedData.unit}
      maxAge={props.maxAge}
      dataset={dataset}
      title={transformedData.title ?? ""}
      height={props.height}
      advanced={props.advanced}
      advancedProps={advancedProps}
      generalProperties={props.generalProperties}
      isMultipleCharts={props.isMultipleCharts}
      stringValueMapper={data?.[0].stringValueMapper ?? dataRange?.[0].stringValueMapper}
      minYAxis={props.minYAxis}
      maxYAxis={props.maxYAxis}
    />
  );
}
